import React, { useMemo } from 'react'
import { FormControl, Grid, InputLabel, MenuItem, Select, TextField } from '@mui/material'
import { type UsFilters, UsTableFilters, useUsRoleTypeLuts } from 'components'
import { type State, UsStateSelect } from 'components/us/common/state-select'
import { type Role } from 'services'
import { filterExecutiveGeneralRoles } from 'utils'

export interface ExecutiveOrgSearchFilters extends UsFilters {
  name: string
  code: string
  role: Role | null
  state: State | null
}

export const initialExecutiveOrgSearchFilters: ExecutiveOrgSearchFilters = {
  name: '',
  code: '',
  role: null,
  state: null,
  page: 1,
  order_field: 'sort_order',
  order_direction: 'ASC'
}

interface Props {
  filters: ExecutiveOrgSearchFilters
  updateFilters: (filters: any) => void
  doFilter: () => Promise<void>
  cleanFilters: () => void
}

export const ExecutiveOrgFilters: React.FC<Props> = ({
  filters,
  updateFilters,
  doFilter,
  cleanFilters
}) => {
  const { lutValues: roleLut } = useUsRoleTypeLuts()

  const roleOptions = useMemo<Role[]>(
    () =>
      filterExecutiveGeneralRoles(Array.from(roleLut.roles.values())).sort((a, b) =>
        a.desc.localeCompare(b.desc)
      ),
    [roleLut.roles]
  )

  return (
    <UsTableFilters
      filters={filters}
      updateFilters={updateFilters}
      doFilter={doFilter}
      cleanFilters={cleanFilters}
    >
      <Grid item xs={4}>
        <TextField
          fullWidth
          label="Name"
          value={filters.name}
          onChange={(event) => { updateFilters({ name: event.target.value }) }}
        />
      </Grid>

      <Grid item xs={4}>
        <TextField
          fullWidth
          label="Code"
          value={filters.code}
          onChange={(event) => { updateFilters({ code: event.target.value }) }}
        />
      </Grid>

      <Grid item xs={4}>
        <FormControl fullWidth>
          <InputLabel>Role</InputLabel>
          <Select
            label="Role"
            value={filters.role?.id ?? ''}
            onChange={(event) => {
              const value = event.target.value
              updateFilters({
                role: value === '' ? null : (roleLut.roles.get(Number(value)) ?? null)
              })
            }}
          >
            <MenuItem value="">None</MenuItem>
            {roleOptions.map((role) => (
              <MenuItem key={role.id} value={role.id}>{role.desc}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={4}>
        <FormControl fullWidth>
          <UsStateSelect
            value={filters.state}
            updateState={(state) => { updateFilters({ state }) }}
            disabled={false}
            onlyUSStates={false}
          />
        </FormControl>
      </Grid>
    </UsTableFilters>
  )
}
